/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  SupersetClient,
  getTimeFormatter,
  TimeFormats,
  ensureIsArray,
} from '@aibi-ui/core';
import moment from 'moment';
import getBootstrapData from './getBootstrapData';
moment.locale('zh-cn');
// ATTENTION: If you change any constants, make sure to also change constants.py

export const EMPTY_STRING = '<empty string>';
export const NULL_STRING = '<NULL>';
export const TRUE_STRING = 'TRUE';
export const FALSE_STRING = 'FALSE';

// moment time format strings
export const SHORT_DATE = 'MMM D, YYYY';
export const SHORT_TIME = 'h:m a';

const DATETIME_FORMATTER = getTimeFormatter(TimeFormats.DATABASE_DATETIME);

export function storeQuery(query) {
  return SupersetClient.post({
    endpoint: '/kv/store/',
    postPayload: { data: query },
  }).then(response => {
    const baseUrl = window.location.origin + window.location.pathname;
    const url = `${baseUrl}?id=${response.json.id}`;
    return url;
  });
}

export function optionLabel(opt) {
  if (opt === null) {
    return NULL_STRING;
  }
  if (opt === '') {
    return EMPTY_STRING;
  }
  if (opt === true) {
    return TRUE_STRING;
  }
  if (opt === false) {
    return FALSE_STRING;
  }
  if (typeof opt !== 'string' && opt.toString) {
    return opt.toString();
  }
  return opt;
}

export function optionValue(opt) {
  if (opt === null) {
    return NULL_STRING;
  }
  return opt;
}

export function optionFromValue(opt) {
  // From a list of options, handles special values & labels
  return { value: optionValue(opt), label: optionLabel(opt) };
}

function getColumnName(column) {
  return column.name || column;
}

export function prepareCopyToClipboardTabularData(data, columns) {
  let result = columns.length
    ? `${columns.map(getColumnName).join('\t')}\n`
    : '';
  for (let i = 0; i < data.length; i += 1) {
    const row = {};
    for (let j = 0; j < columns.length; j += 1) {
      // JavaScript does not maintain the order of a mixed set of keys (i.e integers and strings)
      // the below function orders the keys based on the column names.
      const key = getColumnName(columns[j]);
      if (key in data[i]) {
        row[j] = data[i][key];
      } else {
        row[j] = data[i][parseFloat(key)];
      }
    }
    result += `${Object.values(row).join('\t')}\n`;
  }
  return result;
}

export function applyFormattingToTabularData(data, timeFormattedColumns) {
  if (
    !data ||
    data.length === 0 ||
    ensureIsArray(timeFormattedColumns).length === 0
  ) {
    return data;
  }

  return data.map(row => ({
    ...row,
    /* eslint-disable no-underscore-dangle */
    ...timeFormattedColumns.reduce((acc, colName) => {
      if (row[colName] !== null && row[colName] !== undefined) {
        acc[colName] = DATETIME_FORMATTER(row[colName]);
      }
      return acc;
    }, {}),
  }));
}

export const noOp = () => undefined;

// Detects the user's OS through the browser
export const detectOS = () => {
  const { appVersion } = navigator;

  // Leveraging this condition because of stackOverflow
  // https://stackoverflow.com/questions/11219582/how-to-detect-my-browser-version-and-operating-system-using-javascript
  if (appVersion.includes('Win')) return 'Windows';
  if (appVersion.includes('Mac')) return 'MacOS';
  if (appVersion.includes('X11')) return 'UNIX';
  if (appVersion.includes('Linux')) return 'Linux';

  return 'Unknown OS';
};

export const isSafari = () => {
  const { userAgent } = navigator;

  return userAgent && /^((?!chrome|android).)*safari/i.test(userAgent);
};

/**
 * 转化当前时间差值
 * @param timeStr
 * @returns {moment.Moment | String}
 */
export const parseRelativeTime = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const parts = timeStr.split(' '); // 按空格拆分字符串
  if (parts.length !== 3 || parts[2] !== 'ago') return null; // 确保格式正确

  let amount = parts[0]; // 可能是 "a", "an" 或 数字
  let unit = parts[1]; // 时间单位

  // 处理 "a" 和 "an"
  if (amount === 'a' || amount === 'an') {
    amount = '1';
  }

  // 处理复数单位，转换为单数
  if (unit.endsWith('s')) {
    unit = unit.slice(0, -1); // 去掉最后一个 's'，如 "hours" -> "hour"
  }

  // 确保 unit 是 moment 支持的时间单位
  const validUnits = ['second', 'minute', 'hour', 'day', 'month', 'year'];

  if (!validUnits.includes(unit)) {
    return null;
  }

  return moment().subtract(parseInt(amount, 10), unit);
};

/** 将英文时间转化成相对时间 */
export const parseRelativeTimeToStr = timeStr => {
  const time = parseRelativeTime(timeStr);
  if (!time) return '';
  if (typeof time === 'string') return time;
  if (time.isValid()) {
    return time.fromNow();
  } else {
    return '';
  }
};

/** 判断当前用户是否为教师 */
export const isTeacher = () => {
  const bootstrapData = getBootstrapData();
  return bootstrapData.user?.type === 1;
};

/** 通过roleName判断是否是管理员角色 */
export const isAdmin = roleName => {
  if (typeof roleName !== 'string') return false;
  return (
    roleName.includes('管理员') ||
    roleName.includes('admin') ||
    roleName.includes('Admin')
  );
};

/**
 * 判断当前用户是否为管理员用户
 * @param {string} username
 * @returns {boolean}
 */
export const isAdminUser = username => {
  if (!username) return false;
  return username === 'admin'
}

/**
 * 复制文字
 * @param text
 * @param onSuccess
 * @param onFailure
 */
export function copyTextToClipboard(text, onSuccess, onFailure) {
  if (navigator.clipboard && window.isSecureContext) {
    // 现代方式
    navigator.clipboard
      .writeText(text)
      .then(() => {
        onSuccess?.();
      })
      .catch(err => {
        console.warn('Clipboard API 失败，尝试回退方法：', err);
        fallbackCopyText(text, onSuccess, onFailure);
      });
  } else {
    // 回退方式
    fallbackCopyText(text, onSuccess, onFailure);
  }
}

function fallbackCopyText(text, onSuccess, onFailure) {
  const textarea = document.createElement('textarea');
  textarea.value = text;

  // 防止跳动
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.setAttribute('readonly', '');
  document.body.appendChild(textarea);

  textarea.select();
  try {
    const result = document.execCommand('copy');
    if (result) {
      onSuccess?.();
    } else {
      onFailure?.();
    }
  } catch (err) {
    console.error('复制失败:', err);
    onFailure?.();
  } finally {
    document.body.removeChild(textarea);
  }
}

/** 格式化姓名
 * @param {string|null=} firstName
 * @param {string|null=} lastName
 */
export function formatFullname(firstName, lastName) {
  return `${firstName || ''}${lastName || ''}`;
}
